import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Table,
  Card,
  Typography,
  Tag,
  Input,
  Select,
  DatePicker,
  Space,
  Statistic,
  Row,
  Col,
  Spin,
  Button,
  Modal,
  Form,
  Popconfirm,
  Drawer,
  Badge,
  Flex,
  List,
  Empty,
  Dropdown
} from 'antd';
import { message } from 'antd';
import { SearchOutlined, ShoppingCartOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, PlusOutlined, FilterOutlined } from '@ant-design/icons';
import { api } from '../utils/api';
import dayjs from 'dayjs';
import { formatToIST, formatDateIST, formatTimeIST } from '../utils/dateUtils';

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

// Mobile Order Card Component
const OrderCard = ({ order, onAction }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'executed': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'cancelled': return '#6b7280';
      case 'rejected': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'executed': return '✅';
      case 'pending': return '⏳';
      case 'cancelled': return '❌';
      case 'rejected': return '🚫';
      default: return '❓';
    }
  };

  const getTypeColor = (type) => {
    return type === 'buy' ? '#10b981' : '#ef4444';
  };

  return (
    <Card
      style={{
        marginBottom: 12,
        borderRadius: '16px',
        border: '1px solid #f1f5f9',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        background: 'white'
      }}
      bodyStyle={{ padding: '16px' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ 
              fontSize: '16px', 
              fontWeight: '700', 
              color: '#1e293b',
              letterSpacing: '0.5px'
            }}>
              {order.symbol}
            </span>
            <Tag 
              color={getTypeColor(order.type)}
              style={{ 
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '11px',
                padding: '2px 8px',
                border: 'none'
              }}
            >
              {order.type?.toUpperCase()}
            </Tag>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: '12px', color: '#64748b' }}>
              {order.userId?.name || 'Unknown User'}
            </span>
            <span style={{ 
              background: getStatusColor(order.status),
              color: 'white',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}>
              {getStatusIcon(order.status)} {order.status?.toUpperCase()}
            </span>
          </div>
        </div>
        
        <Dropdown
          menu={{
            items: [
              {
                key: 'edit',
                label: 'Edit Order',
                icon: <EditOutlined />,
                onClick: () => onAction('edit', order)
              },
              {
                key: 'delete',
                label: 'Delete Order',
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => onAction('delete', order)
              }
            ]
          }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button
            type="text"
            icon={<span style={{ fontSize: '16px' }}>⋮</span>}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
        </Dropdown>
      </div>
      
      <Row gutter={[8, 8]}>
        <Col span={12}>
          <div style={{ textAlign: 'center', padding: '8px', background: '#f8fafc', borderRadius: '8px' }}>
            <div style={{ fontSize: '11px', color: '#64748b', marginBottom: 2 }}>Quantity</div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
              {order.quantity?.toLocaleString()}
            </div>
          </div>
        </Col>
        <Col span={12}>
          <div style={{ textAlign: 'center', padding: '8px', background: '#f8fafc', borderRadius: '8px' }}>
            <div style={{ fontSize: '11px', color: '#64748b', marginBottom: 2 }}>Price</div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
              ₹{order.executedPrice || order.price}
            </div>
          </div>
        </Col>
      </Row>
      
      <div style={{ 
        marginTop: 12, 
        paddingTop: 12, 
        borderTop: '1px solid #f1f5f9',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ fontSize: '11px', color: '#64748b' }}>
          {formatDateIST(order.createdAt)}
        </div>
        <div style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>
          Total: ₹{((order.executedPrice || order.price) * order.quantity).toLocaleString()}
        </div>
      </div>
    </Card>
  );
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [users, setUsers] = useState([]);
  const [form] = Form.useForm();
  const [createForm] = Form.useForm();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('cards');

  // Responsive handler
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setViewMode(mobile ? 'cards' : 'table');
    };

    const throttledResize = throttle(handleResize, 100);
    window.addEventListener('resize', throttledResize);
    handleResize();

    return () => window.removeEventListener('resize', throttledResize);
  }, []);

  // Throttle function
  const throttle = (func, delay) => {
    let timeoutId;
    let lastExecTime = 0;
    return function (...args) {
      const currentTime = Date.now();
      if (currentTime - lastExecTime > delay) {
        func.apply(this, args);
        lastExecTime = currentTime;
      } else {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          func.apply(this, args);
          lastExecTime = Date.now();
        }, delay - (currentTime - lastExecTime));
      }
    };
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    fetchOrders();
    fetchUsers();
  }, []);

  useEffect(() => {
    filterOrders();
  }, [orders, debouncedSearchText, statusFilter, typeFilter, dateRange]);

  const fetchOrders = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const params = forceRefresh ? { forceRefresh: true } : {};
      const response = await api.getOrders(params);
      setOrders(response.data.orders || response.data);
      
      if (forceRefresh) {
        message.success('Orders refreshed successfully');
      }
    } catch (error) {
      message.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.getUsers();
      setUsers(response.data.users || response.data || []);
    } catch (error) {
      message.error('Failed to fetch users');
      setUsers([]); // Ensure users is always an array
    }
  };

  // Memoized search handler
  const handleSearchChange = useCallback((e) => setSearchText(e.target.value), []);

  // Memoized filter function
  const filterOrders = useCallback(() => {
    let filtered = [...orders];

    // Search filter
    if (debouncedSearchText) {
      filtered = filtered.filter(order =>
        order.symbol?.toLowerCase().includes(debouncedSearchText.toLowerCase()) ||
        order.userId?.name?.toLowerCase().includes(debouncedSearchText.toLowerCase()) ||
        order.userId?.email?.toLowerCase().includes(debouncedSearchText.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(order => order.type === typeFilter);
    }

    // Date range filter
    if (dateRange && dateRange.length === 2) {
      const [startDate, endDate] = dateRange;
      filtered = filtered.filter(order => {
        const orderDate = dayjs(order.createdAt);
        return orderDate.isAfter(startDate.startOf('day')) &&
          orderDate.isBefore(endDate.endOf('day'));
      });
    }

    setFilteredOrders(filtered);
  }, [orders, debouncedSearchText, statusFilter, typeFilter, dateRange]);

  // Memoized stats calculation for performance
  const stats = useMemo(() => {
    const totalOrders = filteredOrders.length;
    const executedOrders = filteredOrders.filter(order => order.status === 'executed').length;
    const pendingOrders = filteredOrders.filter(order => order.status === 'pending').length;
    const totalValue = filteredOrders
      .filter(order => order.status === 'executed')
      .reduce((sum, order) => sum + (order.quantity * (order.executedPrice || order.price) * (order.lotSize || 1)), 0);
    const totalPnL = filteredOrders
      .filter(order => order.status === 'executed' && order.pnl !== undefined)
      .reduce((sum, order) => sum + order.pnl, 0);

    return { totalOrders, executedOrders, pendingOrders, totalValue, totalPnL };
  }, [filteredOrders]);

  const handleEditOrder = (order) => {
    setSelectedOrder(order);
    setEditModalVisible(true);
    form.setFieldsValue({
      symbol: order.symbol,
      type: order.type,
      orderType: order.orderType,
      quantity: order.quantity,
      price: order.price,
      executedPrice: order.executedPrice,
      status: order.status
    });
  };



  const handleCreateOrder = async (values) => {
    try {
      const response = await api.post('/api/orders/admin/create', values);
      message.success('Order created successfully');
      
      // Show additional message if order was executed
      if (values.status === 'executed') {
        message.info('User portfolio has been automatically updated with the executed order.');
      }
      
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchOrders();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to create order');
    }
  };

  const handleUpdateOrder = async (values) => {
    try {
      const response = await api.put(`/api/orders/admin/${selectedOrder._id}`, values);
      
      if (response.data.portfolioUpdated) {
        message.success('Order updated successfully! User portfolio automatically updated.');
        message.info(`Portfolio changes: ${selectedOrder.type.toUpperCase()} ${values.quantity || selectedOrder.quantity} ${selectedOrder.symbol} at ₹${values.executedPrice || values.price || selectedOrder.price}`);
      } else {
        message.success('Order updated successfully.');
      }
      
      setEditModalVisible(false);
      fetchOrders();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to update order');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      await api.delete(`/api/orders/admin/${orderId}`);
      message.success('Order deleted successfully');
      message.info('User portfolio has been automatically updated (if order was executed).');
      fetchOrders();
    } catch (error) {
      message.error('Failed to delete order');
    }
  };

  const columns = [
    {
      title: 'Order ID',
      dataIndex: '_id',
      key: '_id',
      render: (id) => (
        <span style={{
          fontFamily: 'monospace',
          fontSize: '12px',
          background: '#f1f5f9',
          padding: '4px 8px',
          borderRadius: '4px',
          color: '#475569'
        }}>
          {id?.slice(-8)}
        </span>
      ),
      width: 100,
    },
    {
      title: 'User',
      dataIndex: ['userId', 'name'],
      key: 'user',
      render: (name, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '12px',
            marginRight: 8
          }}>
            {name?.charAt(0) || 'U'}
          </div>
          <div>
            <div style={{ fontWeight: '600', fontSize: '14px' }}>{name || 'N/A'}</div>
            <small style={{ color: '#64748b', fontSize: '12px' }}>
              {record.userId?.email}
            </small>
          </div>
        </div>
      ),
    },
    {
      title: 'Instrument',
      key: 'instrument',
      render: (_, record) => (
        <div>
          <Tag color="blue" style={{ fontWeight: 'bold', marginBottom: 4 }}>
            {record.symbol}
          </Tag>
          <div style={{ fontSize: '12px', color: '#64748b' }}>
            {record.name}
          </div>
          <div style={{ fontSize: '11px', color: '#94a3b8' }}>
            {record.segment} • {record.instrumentType}
          </div>
          {record.strikePrice && record.optionType && (
            <div style={{ fontSize: '11px', color: record.optionType === 'CE' ? '#059669' : '#dc2626' }}>
              Strike: ₹{record.strikePrice} {record.optionType}
            </div>
          )}
          {record.expiryDate && (
            <div style={{ fontSize: '11px', color: '#7c3aed' }}>
              Expiry: {record.expiryDate}
            </div>
          )}
        </div>
      ),
      width: 200,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={type === 'buy' ? 'green' : 'red'} style={{ fontWeight: 'bold' }}>
          {type?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Order Type',
      dataIndex: 'orderType',
      key: 'orderType',
      render: (orderType) => (
        <Tag color="purple" style={{ fontWeight: 'bold' }}>{orderType?.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
      render: (quantity) => (
        <span style={{ fontWeight: '600' }}>{quantity}</span>
      ),
    },
    {
      title: 'Price Details',
      key: 'priceDetails',
      render: (_, record) => (
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: '600', color: '#059669', fontSize: '14px' }}>
            Order: ₹{record.price?.toFixed(2)}
          </div>
          {record.executedPrice > 0 && (
            <div style={{ fontWeight: '600', color: '#7c3aed', fontSize: '13px' }}>
              Executed: ₹{record.executedPrice?.toFixed(2)}
            </div>
          )}
          {record.currentPrice > 0 && (
            <div style={{ fontWeight: '500', color: '#64748b', fontSize: '12px' }}>
              Current: ₹{record.currentPrice?.toFixed(2)}
            </div>
          )}
          {record.lotSize > 1 && (
            <div style={{ fontSize: '11px', color: '#94a3b8' }}>
              Lot Size: {record.lotSize}
            </div>
          )}
        </div>
      ),
      align: 'right',
      width: 140,
    },
    {
      title: 'Value & P/L',
      key: 'valueAndPnl',
      render: (_, record) => {
        const totalValue = record.quantity * (record.executedPrice || record.price) * (record.lotSize || 1);
        const pnlColor = record.pnl >= 0 ? '#059669' : '#dc2626';
        return (
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: '600', color: '#7c3aed', fontSize: '14px' }}>
              ₹{totalValue.toFixed(2)}
            </div>
            {record.status === 'executed' && record.pnl !== undefined && (
              <>
                <div style={{ fontWeight: '600', color: pnlColor, fontSize: '13px' }}>
                  {record.pnl >= 0 ? '+' : ''}₹{record.pnl?.toFixed(2)}
                </div>
                <div style={{ fontSize: '11px', color: pnlColor }}>
                  {record.pnlPercent >= 0 ? '+' : ''}{record.pnlPercent?.toFixed(2)}%
                </div>
              </>
            )}
          </div>
        );
      },
      align: 'right',
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const color = {
          executed: 'green',
          pending: 'orange',
          cancelled: 'red',
          rejected: 'red',
        }[status] || 'default';
        return <Tag color={color} style={{ fontWeight: 'bold' }}>{status?.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (
        <div>
          <div style={{ fontSize: '14px', fontWeight: '500' }}>
            {formatDateIST(date)}
          </div>
          <small style={{ color: '#64748b' }}>
            {formatTimeIST(date)}
          </small>
        </div>
      ),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditOrder(record)}
            style={{ borderRadius: '6px' }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this order?"
            onConfirm={() => handleDeleteOrder(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
              style={{ borderRadius: '6px' }}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
      width: 180,
    },
  ];

  // Memoized callbacks for performance
  const handleOrderAction = useCallback((action, order) => {
    switch (action) {
      case 'edit':
        handleEditOrder(order);
        break;
      case 'delete':
        handleDeleteOrder(order._id);
        break;
      default:
        break;
    }
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="fade-in" style={{ padding: '0 4px' }}>
      {/* Modern Header */}
      <div className="orders-header" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: isMobile ? '20px' : '24px',
        padding: isMobile ? '24px 20px' : '40px 32px',
        marginBottom: '24px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: 'rgba(255,255,255,0.08)',
          borderRadius: '50%',
          transform: 'translate(50%, -50%)'
        }} />
        
        <Flex justify="space-between" align="center" wrap="wrap" gap={16}>
          <div style={{ zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
              <div style={{
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '16px',
                padding: '12px',
                backdropFilter: 'blur(10px)'
              }}>
                <ShoppingCartOutlined style={{ fontSize: isMobile ? '24px' : '28px' }} />
              </div>
              <Title level={isMobile ? 3 : 1} style={{ 
                margin: 0, 
                color: 'white', 
                fontSize: isMobile ? '22px' : '32px', 
                fontWeight: 800,
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                letterSpacing: '-0.5px'
              }}>
                Orders Management
              </Title>
            </div>
            <p style={{ 
              color: 'rgba(255,255,255,0.85)', 
              margin: 0, 
              fontSize: isMobile ? '14px' : '16px',
              textShadow: '0 1px 2px rgba(0,0,0,0.1)',
              fontWeight: 400
            }}>
              Monitor and manage all trading orders
            </p>
          </div>
          
          <Space size={isMobile ? 8 : 12} style={{ zIndex: 1 }}>
            {isMobile ? (
              <>
                <Button
                  type="text"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateModalVisible(true)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    width: '44px',
                    height: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                />
                <Button
                  type="text"
                  icon={<ReloadOutlined />}
                  onClick={() => fetchOrders(true)}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    width: '44px',
                    height: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                />
              </>
            ) : (
              <>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateModalVisible(true)}
                  style={{
                    background: 'rgba(255,255,255,0.9)',
                    border: 'none',
                    color: '#667eea',
                    borderRadius: '12px',
                    height: '44px',
                    fontWeight: 600
                  }}
                >
                  Create Order
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => fetchOrders(true)}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: 'white',
                    borderRadius: '12px',
                    height: '44px',
                    fontWeight: '600'
                  }}
                  loading={loading}
                >
                  Refresh
                </Button>
              </>
            )}
          </Space>
        </Flex>
      </div>

      {/* Enhanced Statistics Cards */}
      <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]} style={{ marginBottom: isMobile ? 16 : 24 }}>
        <Col xs={12} sm={12} md={6} lg={6}>
          <Card 
            className="slide-up stats-card-modern" 
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              border: 'none',
              borderRadius: isMobile ? '16px' : '20px',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              minHeight: isMobile ? '100px' : '120px',
              height: isMobile ? '100px' : '120px'
            }}
            bodyStyle={{ 
              padding: isMobile ? '16px' : '20px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <div style={{
              position: 'absolute',
              top: isMobile ? '-5px' : '-10px',
              right: isMobile ? '-5px' : '-10px',
              fontSize: isMobile ? '40px' : '60px',
              opacity: 0.2,
              color: 'white'
            }}>
              📊
            </div>
            <Statistic
              title={
                <span style={{ 
                  color: 'rgba(255,255,255,0.9)', 
                  fontWeight: 600,
                  fontSize: isMobile ? '11px' : '14px'
                }}>
                  Total Orders
                </span>
              }
              value={stats.totalOrders}
              precision={0}
              valueStyle={{ 
                color: 'white', 
                fontSize: isMobile ? '18px' : '24px', 
                fontWeight: 'bold',
                lineHeight: 1.2
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6} lg={6}>
          <Card 
            className="slide-up stats-card-modern" 
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              border: 'none',
              borderRadius: isMobile ? '16px' : '20px',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              minHeight: isMobile ? '100px' : '120px',
              height: isMobile ? '100px' : '120px'
            }}
            bodyStyle={{ 
              padding: isMobile ? '16px' : '20px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <div style={{
              position: 'absolute',
              top: isMobile ? '-5px' : '-10px',
              right: isMobile ? '-5px' : '-10px',
              fontSize: isMobile ? '40px' : '60px',
              opacity: 0.2,
              color: 'white'
            }}>
              ✅
            </div>
            <Statistic
              title={
                <span style={{ 
                  color: 'rgba(255,255,255,0.9)', 
                  fontWeight: 600,
                  fontSize: isMobile ? '11px' : '14px'
                }}>
                  Executed
                </span>
              }
              value={stats.executedOrders}
              precision={0}
              valueStyle={{ 
                color: 'white', 
                fontSize: isMobile ? '18px' : '24px', 
                fontWeight: 'bold',
                lineHeight: 1.2
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6} lg={6}>
          <Card 
            className="slide-up stats-card-modern" 
            style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              border: 'none',
              borderRadius: isMobile ? '16px' : '20px',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              minHeight: isMobile ? '100px' : '120px',
              height: isMobile ? '100px' : '120px'
            }}
            bodyStyle={{ 
              padding: isMobile ? '16px' : '20px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <div style={{
              position: 'absolute',
              top: isMobile ? '-5px' : '-10px',
              right: isMobile ? '-5px' : '-10px',
              fontSize: isMobile ? '40px' : '60px',
              opacity: 0.2,
              color: 'white'
            }}>
              ⏳
            </div>
            <Statistic
              title={
                <span style={{ 
                  color: 'rgba(255,255,255,0.9)', 
                  fontWeight: 600,
                  fontSize: isMobile ? '11px' : '14px'
                }}>
                  Pending
                </span>
              }
              value={stats.pendingOrders}
              precision={0}
              valueStyle={{ 
                color: 'white', 
                fontSize: isMobile ? '18px' : '24px', 
                fontWeight: 'bold',
                lineHeight: 1.2
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6} lg={6}>
          <Card 
            className="slide-up stats-card-modern" 
            style={{
              background: stats.totalPnL >= 0
                ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              border: 'none',
              borderRadius: isMobile ? '16px' : '20px',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              minHeight: isMobile ? '100px' : '120px',
              height: isMobile ? '100px' : '120px'
            }}
            bodyStyle={{ 
              padding: isMobile ? '16px' : '20px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <div style={{
              position: 'absolute',
              top: isMobile ? '-5px' : '-10px',
              right: isMobile ? '-5px' : '-10px',
              fontSize: isMobile ? '40px' : '60px',
              opacity: 0.2,
              color: 'white'
            }}>
              {stats.totalPnL >= 0 ? '💰' : '📉'}
            </div>
            <Statistic
              title={
                <span style={{ 
                  color: 'rgba(255,255,255,0.9)', 
                  fontWeight: 600,
                  fontSize: isMobile ? '11px' : '14px'
                }}>
                  Total P/L
                </span>
              }
              value={Math.abs(stats.totalPnL)}
              precision={2}
              prefix={stats.totalPnL >= 0 ? '+₹' : '-₹'}
              valueStyle={{ 
                color: 'white', 
                fontSize: isMobile ? '16px' : '22px', 
                fontWeight: 'bold',
                lineHeight: 1.2
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Mobile-Friendly Filters */}
      {isMobile ? (
        <>
          <Card 
            style={{ 
              marginBottom: 20, 
              borderRadius: '20px',
              border: 'none',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)'
            }} 
            className="slide-up"
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ marginBottom: 16 }}>
              <Search
                placeholder="🔍 Search orders by symbol, user, or email..."
                allowClear
                onChange={handleSearchChange}
                style={{ 
                  borderRadius: '16px',
                  border: '2px solid #f1f5f9',
                  background: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  fontSize: '16px'
                }}
                size="large"
              />
            </div>
            <Button
              block
              icon={<FilterOutlined />}
              onClick={() => setShowFilters(!showFilters)}
              style={{ 
                borderRadius: '16px',
                height: '48px',
                fontSize: '16px',
                fontWeight: '600',
                background: showFilters 
                  ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' 
                  : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                borderColor: showFilters ? '#3b82f6' : '#e2e8f0',
                color: showFilters ? 'white' : '#475569',
                border: 'none',
                boxShadow: showFilters 
                  ? '0 4px 12px rgba(59, 130, 246, 0.3)' 
                  : '0 2px 8px rgba(0,0,0,0.06)',
                transition: 'all 0.3s ease'
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                Filters 
                {(statusFilter !== 'all' || typeFilter !== 'all' || dateRange) && (
                  <Badge 
                    count={(statusFilter !== 'all' ? 1 : 0) + (typeFilter !== 'all' ? 1 : 0) + (dateRange ? 1 : 0)} 
                    style={{ 
                      backgroundColor: '#ef4444',
                      fontSize: '10px',
                      minWidth: '18px',
                      height: '18px',
                      lineHeight: '18px'
                    }} 
                  />
                )}
              </span>
            </Button>
          </Card>
          
          <Drawer
            title={
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 12,
                color: 'white'
              }}>
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '12px',
                  padding: '8px',
                  backdropFilter: 'blur(10px)'
                }}>
                  <FilterOutlined style={{ fontSize: '18px' }} />
                </div>
                <span style={{ fontSize: '18px', fontWeight: '600' }}>Filter Orders</span>
              </div>
            }
            placement="bottom"
            onClose={() => setShowFilters(false)}
            open={showFilters}
            height={420}
            styles={{
              header: {
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderBottom: 'none',
                borderRadius: '20px 20px 0 0',
                padding: '20px 24px'
              },
              body: {
                padding: '24px',
                background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)'
              }
            }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size={20}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: 12, 
                  fontWeight: 700,
                  color: '#1e293b',
                  fontSize: '15px',
                  letterSpacing: '0.5px'
                }}>📊 Order Status</label>
                <Select
                  placeholder="Select order status"
                  style={{ 
                    width: '100%',
                    borderRadius: '16px'
                  }}
                  value={statusFilter}
                  onChange={setStatusFilter}
                  size="large"
                >
                  <Option value="all">🌐 All Status</Option>
                  <Option value="pending">⏳ Pending</Option>
                  <Option value="executed">✅ Executed</Option>
                  <Option value="cancelled">❌ Cancelled</Option>
                  <Option value="rejected">🚫 Rejected</Option>
                </Select>
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: 12, 
                  fontWeight: 700,
                  color: '#1e293b',
                  fontSize: '15px',
                  letterSpacing: '0.5px'
                }}>📈 Order Type</label>
                <Select
                  placeholder="Select order type"
                  style={{ 
                    width: '100%',
                    borderRadius: '16px'
                  }}
                  value={typeFilter}
                  onChange={setTypeFilter}
                  size="large"
                >
                  <Option value="all">🔄 All Types</Option>
                  <Option value="buy">📈 Buy</Option>
                  <Option value="sell">📉 Sell</Option>
                </Select>
              </div>
              
              <Button 
                block 
                type="primary" 
                onClick={() => setShowFilters(false)}
                style={{
                  height: '52px',
                  borderRadius: '16px',
                  marginTop: '20px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  border: 'none',
                  fontWeight: '700',
                  fontSize: '16px',
                  boxShadow: '0 4px 16px rgba(59, 130, 246, 0.4)',
                  letterSpacing: '0.5px'
                }}
              >
                ✨ Apply Filters
              </Button>
            </Space>
          </Drawer>
        </>
      ) : (
        <Card
          style={{
            marginBottom: 24,
            borderRadius: '16px',
            border: '1px solid #f0f0f0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}
          className="slide-up"
          bodyStyle={{ padding: '20px' }}
        >
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} sm={24} md={24} lg={12} xl={10}>
              <Search
                placeholder="Search orders by symbol, user, or email..."
                allowClear
                onChange={handleSearchChange}
                prefix={<SearchOutlined />}
                style={{ 
                  borderRadius: '12px'
                }}
                size="large"
              />
            </Col>
            
            <Col xs={12} sm={8} md={6} lg={4} xl={3}>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ 
                  width: '100%', 
                  borderRadius: '12px'
                }}
                placeholder="Status"
                size="large"
              >
                <Option value="all">All Status</Option>
                <Option value="pending">Pending</Option>
                <Option value="executed">Executed</Option>
                <Option value="cancelled">Cancelled</Option>
                <Option value="rejected">Rejected</Option>
              </Select>
            </Col>
            
            <Col xs={12} sm={8} md={6} lg={4} xl={3}>
              <Select
                value={typeFilter}
                onChange={setTypeFilter}
                style={{ 
                  width: '100%', 
                  borderRadius: '12px'
                }}
                placeholder="Type"
                size="large"
              >
                <Option value="all">All Types</Option>
                <Option value="buy">Buy</Option>
                <Option value="sell">Sell</Option>
              </Select>
            </Col>
            
            <Col xs={24} sm={8} md={12} lg={8} xl={8}>
              <Space wrap style={{ width: '100%', justifyContent: window.innerWidth > 1200 ? 'flex-end' : 'flex-start' }}>
                <RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  style={{ borderRadius: '12px' }}
                  size="large"
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateModalVisible(true)}
                  style={{ 
                    borderRadius: '12px',
                    height: '44px',
                    fontWeight: '600'
                  }}
                >
                  Create Order
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => fetchOrders(true)}
                  style={{ 
                    borderRadius: '12px',
                    height: '44px',
                    fontWeight: '600'
                  }}
                  loading={loading}
                >
                  Refresh
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      )}

      {/* Orders List/Table */}
      {isMobile ? (
        <div>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: 16,
            padding: '0 4px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShoppingCartOutlined style={{ color: '#3b82f6', fontSize: '18px' }} />
              <span style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                All Orders ({filteredOrders.length})
              </span>
            </div>
          </div>
          
          {filteredOrders.length === 0 ? (
            <Card style={{ 
              textAlign: 'center', 
              padding: '40px 20px',
              borderRadius: '16px',
              border: '2px dashed #e2e8f0'
            }}>
              <Empty 
                description={
                  <span style={{ color: '#64748b', fontSize: '14px' }}>
                    No orders found
                  </span>
                }
              />
            </Card>
          ) : (
            <List
              dataSource={filteredOrders}
              renderItem={(order) => (
                <OrderCard 
                  key={order._id} 
                  order={order} 
                  onAction={handleOrderAction}
                />
              )}
              pagination={{
                total: filteredOrders.length,
                pageSize: isMobile ? 8 : 12,
                showSizeChanger: false,
                showQuickJumper: false,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                style: { textAlign: 'center', marginTop: 16 }
              }}
            />
          )}
        </div>
      ) : (
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShoppingCartOutlined style={{ color: '#3b82f6' }} />
              <span>All Orders ({filteredOrders.length})</span>
            </div>
          }
          className="slide-up"
        >
          <Table
            columns={columns}
            dataSource={filteredOrders}
            rowKey="_id"
            pagination={{
              total: filteredOrders.length,
              pageSize: 25,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} orders`,
            }}
            scroll={{ x: 1400 }}
            size="middle"
          />
        </Card>
      )}

      {/* Create Order Modal */}
      <Modal
        title={
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 12,
            color: isMobile ? 'white' : '#1e293b'
          }}>
            <div style={{
              background: isMobile ? 'rgba(255,255,255,0.2)' : '#f8fafc',
              borderRadius: '12px',
              padding: '8px',
              backdropFilter: isMobile ? 'blur(10px)' : 'none'
            }}>
              <PlusOutlined style={{ fontSize: '18px', color: isMobile ? 'white' : '#10b981' }} />
            </div>
            <div>
              <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '600' }}>
                📝 Create New Order
              </div>
              <div style={{ 
                fontSize: isMobile ? '12px' : '14px', 
                opacity: 0.8,
                marginTop: 2
              }}>
                Add order to the system
              </div>
            </div>
          </div>
        }
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 900}
        style={{ 
          top: isMobile ? 0 : 20,
          maxHeight: isMobile ? '100vh' : 'auto'
        }}
        styles={{
          header: isMobile ? {
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            borderBottom: 'none',
            borderRadius: '0',
            padding: '20px 24px',
            marginBottom: 0
          } : {
            borderBottom: '1px solid #f0f0f0',
            padding: '20px 24px'
          },
          body: { 
            padding: isMobile ? '20px' : '24px',
            background: isMobile ? 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)' : 'white',
            maxHeight: isMobile ? 'calc(100vh - 120px)' : '80vh',
            overflowY: 'auto'
          },
          content: {
            borderRadius: isMobile ? '0' : '12px',
            overflow: 'hidden'
          }
        }}
      >
        <div style={{
          marginBottom: 24,
          padding: 12,
          background: '#f0f9ff',
          borderRadius: '6px',
          border: '1px solid #0ea5e9',
          fontSize: '13px',
          color: '#0c4a6e'
        }}>
          <strong>🚀 Smart Portfolio Management:</strong> Creating orders with "Executed" status automatically updates user portfolios and balances. All calculations are handled automatically!
        </div>
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateOrder}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                name="userId"
                label={<span style={{ fontWeight: 600, color: '#1e293b' }}>👤 User</span>}
                rules={[{ required: true, message: 'Please select user' }]}
              >
                <Select 
                  placeholder="Select user" 
                  showSearch 
                  optionFilterProp="children"
                  style={{
                    height: isMobile ? '48px' : '40px'
                  }}
                  size={isMobile ? 'large' : 'middle'}
                >
                  {Array.isArray(users) && users.map(user => (
                    <Option key={user._id} value={user._id}>
                      {user.name} ({user.email}) - Balance: ₹{user.balance?.toFixed(2)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                name="symbol"
                label={<span style={{ fontWeight: 600, color: '#1e293b' }}>📈 Symbol</span>}
                rules={[{ required: true, message: 'Please enter symbol' }]}
              >
                <Input 
                  placeholder="e.g., RELIANCE"
                  style={{
                    height: isMobile ? '48px' : '40px',
                    borderRadius: '12px',
                    fontSize: isMobile ? '16px' : '14px'
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                name="type"
                label={<span style={{ fontWeight: 600, color: '#1e293b' }}>🔄 Order Type</span>}
                rules={[{ required: true, message: 'Please select type' }]}
              >
                <Select 
                  placeholder="Select type"
                  style={{
                    height: isMobile ? '48px' : '40px'
                  }}
                  size={isMobile ? 'large' : 'middle'}
                >
                  <Option value="buy">🟢 Buy</Option>
                  <Option value="sell">🔴 Sell</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                name="orderType"
                label={<span style={{ fontWeight: 600, color: '#1e293b' }}>📊 Order Category</span>}
                rules={[{ required: true, message: 'Please select category' }]}
              >
                <Select 
                  placeholder="Select category"
                  style={{
                    height: isMobile ? '48px' : '40px'
                  }}
                  size={isMobile ? 'large' : 'middle'}
                >
                  <Option value="market">⚡ Market</Option>
                  <Option value="limit">🎯 Limit</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={8} lg={8}>
              <Form.Item
                name="quantity"
                label={<span style={{ fontWeight: 600, color: '#1e293b' }}>📦 Quantity</span>}
                rules={[{ required: true, message: 'Please enter quantity' }]}
              >
                <Input 
                  type="number" 
                  placeholder="e.g., 10"
                  style={{
                    height: isMobile ? '48px' : '40px',
                    borderRadius: '12px',
                    fontSize: isMobile ? '16px' : '14px'
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={8}>
              <Form.Item
                name="price"
                label={<span style={{ fontWeight: 600, color: '#1e293b' }}>💰 Price</span>}
                rules={[{ required: true, message: 'Please enter price' }]}
              >
                <Input 
                  type="number" 
                  step="0.01" 
                  placeholder="e.g., 1390.50"
                  prefix="₹"
                  style={{
                    height: isMobile ? '48px' : '40px',
                    borderRadius: '12px',
                    fontSize: isMobile ? '16px' : '14px'
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={8}>
              <Form.Item
                name="status"
                label={<span style={{ fontWeight: 600, color: '#1e293b' }}>📋 Status</span>}
                rules={[{ required: true, message: 'Please select status' }]}
                help={createForm.getFieldValue('status') === 'executed' ? "⚠️ Setting to 'Executed' will automatically update user's portfolio and balance" : ""}
              >
                <Select 
                  placeholder="Select status"
                  style={{
                    height: isMobile ? '48px' : '40px'
                  }}
                  size={isMobile ? 'large' : 'middle'}
                >
                  <Option value="pending">⏳ Pending</Option>
                  <Option value="executed">✅ Executed (Auto-updates Portfolio)</Option>
                  <Option value="cancelled">❌ Cancelled</Option>
                  <Option value="rejected">🚫 Rejected</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item 
                name="name" 
                label={<span style={{ fontWeight: 600, color: '#1e293b' }}>🏢 Instrument Name</span>}
              >
                <Input 
                  placeholder="e.g., Reliance Industries"
                  style={{
                    height: isMobile ? '48px' : '40px',
                    borderRadius: '12px',
                    fontSize: isMobile ? '16px' : '14px'
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item 
                name="segment" 
                label={<span style={{ fontWeight: 600, color: '#1e293b' }}>🏛️ Segment</span>}
              >
                <Select 
                  placeholder="Select segment"
                  style={{
                    height: isMobile ? '48px' : '40px'
                  }}
                  size={isMobile ? 'large' : 'middle'}
                >
                  <Option value="NSE_EQ">🏢 NSE Equity</Option>
                  <Option value="BSE_EQ">🏛️ BSE Equity</Option>
                  <Option value="NSE_FO">📈 NSE F&O</Option>
                  <Option value="MCX_FO">🥇 MCX Commodity</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div style={{ 
            marginTop: 24,
            padding: isMobile ? '16px 0' : '20px 0',
            borderTop: '1px solid #f0f0f0'
          }}>
            {isMobile ? (
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  block
                  style={{
                    height: '52px',
                    borderRadius: '16px',
                    fontSize: '16px',
                    fontWeight: '700',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    border: 'none',
                    boxShadow: '0 4px 16px rgba(16, 185, 129, 0.4)'
                  }}
                >
                  ✨ Create Order
                </Button>
                <Button 
                  onClick={() => setCreateModalVisible(false)}
                  block
                  style={{
                    height: '48px',
                    borderRadius: '16px',
                    fontSize: '16px',
                    fontWeight: '600',
                    background: '#f8fafc',
                    border: '2px solid #e2e8f0',
                    color: '#64748b'
                  }}
                >
                  ❌ Cancel
                </Button>
              </Space>
            ) : (
              <div style={{ textAlign: 'right' }}>
                <Space size={12}>
                  <Button 
                    onClick={() => setCreateModalVisible(false)}
                    style={{
                      height: '44px',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontWeight: '600',
                      padding: '0 24px'
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    style={{
                      height: '44px',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontWeight: '600',
                      padding: '0 24px',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      border: 'none'
                    }}
                  >
                    Create Order
                  </Button>
                </Space>
              </div>
            )}
          </div>
        </Form>
      </Modal>

      {/* Edit Order Modal */}
      <Modal
        title={
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 12,
            color: isMobile ? 'white' : '#1e293b'
          }}>
            <div style={{
              background: isMobile ? 'rgba(255,255,255,0.2)' : '#f8fafc',
              borderRadius: '12px',
              padding: '8px',
              backdropFilter: isMobile ? 'blur(10px)' : 'none'
            }}>
              <EditOutlined style={{ fontSize: '18px', color: isMobile ? 'white' : '#3b82f6' }} />
            </div>
            <div>
              <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '600' }}>
                ✏️ Edit Order
              </div>
              <div style={{ 
                fontSize: isMobile ? '12px' : '14px', 
                opacity: 0.8,
                marginTop: 2
              }}>
                Modify order details
              </div>
            </div>
          </div>
        }
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 900}
        style={{ 
          top: isMobile ? 0 : 20,
          maxHeight: isMobile ? '100vh' : 'auto'
        }}
        styles={{
          header: isMobile ? {
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            borderBottom: 'none',
            borderRadius: '0',
            padding: '20px 24px',
            marginBottom: 0
          } : {
            borderBottom: '1px solid #f0f0f0',
            padding: '20px 24px'
          },
          body: { 
            padding: isMobile ? '20px' : '24px',
            background: isMobile ? 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)' : 'white',
            maxHeight: isMobile ? 'calc(100vh - 120px)' : '80vh',
            overflowY: 'auto'
          },
          content: {
            borderRadius: isMobile ? '0' : '12px',
            overflow: 'hidden'
          }
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateOrder}
        >
          <div style={{
            marginBottom: isMobile ? 16 : 24,
            padding: isMobile ? 16 : 20,
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            borderRadius: isMobile ? '12px' : '16px',
            border: '1px solid #0ea5e9',
            boxShadow: '0 2px 8px rgba(14, 165, 233, 0.1)'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: isMobile ? 'flex-start' : 'center', 
              gap: 12, 
              marginBottom: 12,
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Tag 
                  color="blue" 
                  style={{ 
                    fontSize: isMobile ? '12px' : '14px', 
                    padding: isMobile ? '6px 12px' : '4px 12px',
                    borderRadius: '20px',
                    fontWeight: '600'
                  }}
                >
                  📈 {selectedOrder?.symbol}
                </Tag>
                <Tag 
                  color={selectedOrder?.type === 'buy' ? 'green' : 'red'}
                  style={{ 
                    fontSize: isMobile ? '12px' : '14px', 
                    padding: isMobile ? '6px 12px' : '4px 12px',
                    borderRadius: '20px',
                    fontWeight: '600'
                  }}
                >
                  {selectedOrder?.type === 'buy' ? '🟢 BUY' : '🔴 SELL'}
                </Tag>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  fontWeight: '600', 
                  fontSize: isMobile ? '14px' : '16px', 
                  color: '#1e293b',
                  marginBottom: 4
                }}>
                  🆔 Order ID: {selectedOrder?._id?.slice(-8)}
                </div>
                <div style={{ 
                  color: '#64748b', 
                  fontSize: isMobile ? '12px' : '14px' 
                }}>
                  👤 {selectedOrder?.userId?.name} ({selectedOrder?.userId?.email})
                </div>
              </div>
            </div>
            <div style={{
              padding: isMobile ? 12 : 16,
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              borderRadius: isMobile ? '8px' : '12px',
              border: '1px solid #f59e0b',
              fontSize: isMobile ? '12px' : '13px',
              color: '#92400e'
            }}>
              <strong>🔄 Smart Portfolio Update:</strong> Any changes to executed orders will automatically update the user's portfolio and balance. The system handles all calculations!
            </div>
          </div>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                name="symbol"
                label={<span style={{ fontWeight: 600, color: '#1e293b' }}>📈 Symbol</span>}
                rules={[{ required: true, message: 'Please enter symbol' }]}
              >
                <Input 
                  placeholder="e.g., RELIANCE"
                  style={{
                    height: isMobile ? '48px' : '40px',
                    borderRadius: '12px',
                    fontSize: isMobile ? '16px' : '14px'
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                name="type"
                label={<span style={{ fontWeight: 600, color: '#1e293b' }}>🔄 Order Type</span>}
                rules={[{ required: true, message: 'Please select type' }]}
              >
                <Select 
                  placeholder="Select type"
                  style={{
                    height: isMobile ? '48px' : '40px'
                  }}
                  size={isMobile ? 'large' : 'middle'}
                >
                  <Option value="buy">🟢 Buy</Option>
                  <Option value="sell">🔴 Sell</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={8} lg={8}>
              <Form.Item
                name="quantity"
                label={<span style={{ fontWeight: 600, color: '#1e293b' }}>📦 Quantity</span>}
                rules={[{ required: true, message: 'Please enter quantity' }]}
              >
                <Input 
                  type="number" 
                  placeholder="e.g., 10"
                  style={{
                    height: isMobile ? '48px' : '40px',
                    borderRadius: '12px',
                    fontSize: isMobile ? '16px' : '14px'
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={8}>
              <Form.Item
                name="price"
                label={<span style={{ fontWeight: 600, color: '#1e293b' }}>💰 Order Price</span>}
                rules={[{ required: true, message: 'Please enter price' }]}
              >
                <Input 
                  type="number" 
                  step="0.01" 
                  placeholder="e.g., 1390.50"
                  prefix="₹"
                  style={{
                    height: isMobile ? '48px' : '40px',
                    borderRadius: '12px',
                    fontSize: isMobile ? '16px' : '14px'
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={8}>
              <Form.Item
                name="executedPrice"
                label={<span style={{ fontWeight: 600, color: '#1e293b' }}>💎 Executed Price</span>}
              >
                <Input 
                  type="number" 
                  step="0.01" 
                  placeholder="e.g., 1392.00"
                  prefix="₹"
                  style={{
                    height: isMobile ? '48px' : '40px',
                    borderRadius: '12px',
                    fontSize: isMobile ? '16px' : '14px'
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                name="orderType"
                label={<span style={{ fontWeight: 600, color: '#1e293b' }}>📊 Order Category</span>}
                rules={[{ required: true, message: 'Please select category' }]}
              >
                <Select 
                  placeholder="Select category"
                  style={{
                    height: isMobile ? '48px' : '40px'
                  }}
                  size={isMobile ? 'large' : 'middle'}
                >
                  <Option value="market">⚡ Market</Option>
                  <Option value="limit">🎯 Limit</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                name="status"
                label={<span style={{ fontWeight: 600, color: '#1e293b' }}>📋 Status</span>}
                rules={[{ required: true, message: 'Please select status' }]}
                help={form.getFieldValue('status') === 'executed' ? "⚠️ Setting to 'Executed' will automatically update user's portfolio and balance" : ""}
              >
                <Select 
                  placeholder="Select status"
                  style={{
                    height: isMobile ? '48px' : '40px'
                  }}
                  size={isMobile ? 'large' : 'middle'}
                >
                  <Option value="pending">⏳ Pending</Option>
                  <Option value="executed">✅ Executed (Auto-updates Portfolio)</Option>
                  <Option value="cancelled">❌ Cancelled</Option>
                  <Option value="rejected">🚫 Rejected</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div style={{ 
            marginTop: 24,
            padding: isMobile ? '16px 0' : '20px 0',
            borderTop: '1px solid #f0f0f0'
          }}>
            {isMobile ? (
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  block
                  style={{
                    height: '52px',
                    borderRadius: '16px',
                    fontSize: '16px',
                    fontWeight: '700',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    border: 'none',
                    boxShadow: '0 4px 16px rgba(59, 130, 246, 0.4)'
                  }}
                >
                  ✏️ Update Order
                </Button>
                <Button 
                  onClick={() => setEditModalVisible(false)}
                  block
                  style={{
                    height: '48px',
                    borderRadius: '16px',
                    fontSize: '16px',
                    fontWeight: '600',
                    background: '#f8fafc',
                    border: '2px solid #e2e8f0',
                    color: '#64748b'
                  }}
                >
                  ❌ Cancel
                </Button>
              </Space>
            ) : (
              <div style={{ textAlign: 'right' }}>
                <Space size={12}>
                  <Button 
                    onClick={() => setEditModalVisible(false)}
                    style={{
                      height: '44px',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontWeight: '600',
                      padding: '0 24px'
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    style={{
                      height: '44px',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontWeight: '600',
                      padding: '0 24px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      border: 'none'
                    }}
                  >
                    Update Order
                  </Button>
                </Space>
              </div>
            )}
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Orders;